import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext/useEffectiveExperience';
import {
  createExperienceEventsPredicate,
  createExperienceQueryKey,
} from '@/utils/queryKeys';

const POLL_INTERVAL_MS = 4000;
// A revoked integration may never stamp lastSyncedAt, so stop after two minutes.
const MAX_POLL_TICKS = 30;

// Initial subscription exports run on a server queue. Refresh the experience
// prefix until that queue settles so event export rows and their Added state
// arrive without requiring a reload.
export const usePendingSubscriptionSync = () => {
  const experience = useEffectiveExperience();
  const queryClient = useQueryClient();

  const hasPendingSync = experience.viewerCalendarSubscriptions.some(
    (subscription) => !subscription.lastSyncedAt
  );
  const { id, publishedSlug } = experience;
  const wasPending = useRef(false);

  useEffect(() => {
    if (!hasPendingSync) {
      if (wasPending.current) {
        wasPending.current = false;
        // The settling experience response may race the queue's final event
        // writes, so refresh only the event queries once more.
        void queryClient.invalidateQueries({
          predicate: createExperienceEventsPredicate({ id, publishedSlug }),
        });
      }
      return;
    }

    wasPending.current = true;
    let ticks = 0;
    const interval = setInterval(() => {
      ticks += 1;
      if (ticks > MAX_POLL_TICKS) {
        clearInterval(interval);
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(id),
      });
      if (publishedSlug) {
        void queryClient.invalidateQueries({
          queryKey: createExperienceQueryKey(publishedSlug),
        });
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasPendingSync, id, publishedSlug, queryClient]);
};
